// Angular
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
// Services
import { ImportOrderService } from '../../../import-order/service/import-order.service';
// Model
import { PaginationModel } from '@cv-invision/cv.component-library.ux';
import { OrderReviewModel } from '../../../import-order/state/model/import-grid.model';
import { ImportColumnsModel } from '../../../import-order/model/import-grid.model';
import { CREATE_MANUAL_ORDER } from '../../model/create-order-form.model';
// State
import { Store } from '@ngxs/store';
// Component
import { CustomStatusImportCellComponent } from '../../../import-order/custom-status-cell/custom-status-cell.component';
// Other
import { BroadcastEventModel, BroadcastService, CustomDateCellComponent, SignalRState } from '@cv-invision/core';
import { Observable, of, Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'cv-create-order-progress-tracker-table',
    templateUrl: './create-order-progress-tracker-table.component.html',
    styleUrls: ['./create-order-progress-tracker-table.component.scss']
})
export class CreateOrderProgressTrackerTableComponent implements OnInit, OnDestroy {
    @ViewChild('orderGrid') agGrid: any;

    channels$: Observable<any> = this.store.select(SignalRState.channelEvents);
    private atComponentDestroy = new Subject<void>();
    private hasInitialLoaded = false;
    private broadcastUnsubscribeFn: (() => void) | undefined;
    gridData: Observable<OrderReviewModel[]> = of([]);
    pagination: PaginationModel = { limit: 10, offset: 0, totalCount: 0 };
    isGridReady = false;
    gridOptions: any;

    constructor(private importOrderService: ImportOrderService, private store: Store, private broadcastService: BroadcastService,) { }

    ngOnInit(): void {
        this.broadcastUnsubscribeFn = this.broadcastService.on(CREATE_MANUAL_ORDER, () => {
            this.loadOrders(0);
        });

        this.channels$
            .pipe(takeUntil(this.atComponentDestroy))
            .subscribe((channels) => {
                if (channels && channels[`orders`]) {
                    if(this.hasInitialLoaded){
                        this.refreshTable();
                    }
                }
            });

        this.setupGridOptions();
        this.gridReady();
        this.refreshTable();
        this.hasInitialLoaded = true;
    }

    /**
     * Configures ag-Grid options including:
     * - column definitions
     * - default column behavior
     * - custom cell components
     */
    private setupGridOptions(): void {
        this.gridOptions = {
            rowHeight: 40,
            rowSelection: 'single',
            enableCellTextSelection: true,
            defaultColDef: {
                resizable: true,
                tooltipComponent: 'customStatusImportCellComponent'
            },
            localeText: {
                noRowsToShow: 'No requested orders to show',
                loadingOoo: 'Loading requested orders...'
            },
            columnDefs: new ImportColumnsModel().columns,
            frameworkComponents: {
                customStatusImportCellComponent: CustomStatusImportCellComponent,
                customDateCellComponent: CustomDateCellComponent
            }
        };
    }

    /**
     * Sets a delay before marking the grid as ready.
     * This helps avoid layout issues when navigating back to the page.
     */
    private gridReady(): void {
        // for some reason the 'sizeColumnsToFit' is not working fine when we go back from inbox section
        // this is hack to avoid a bad rendering
        setTimeout(() => {
            this.isGridReady = true;
        }, 500);
    }

    /**
       * Called when the grid is initialized.
       * Stores the grid API reference and resizes columns to fit.
       *
       * @param params The grid initialization parameters including API reference
       */
    onInitializeGrid(params: any): void {
        this.agGrid = params.api;
        this.resizeToFitColumns();

    }

    /**
    * Updates pagination state and triggers data load for the new page.
    *
    * @param paginationModel The updated pagination settings
    */
    onPaginationChange(paginationModel: PaginationModel): void {
        this.pagination = paginationModel;
        this.loadOrders(paginationModel.offset);
    }

    /**
    * Loads and filters order data for the specified page.
    * Only orders created manually (externalProvider and externalTrackingProvider === 'Manual') will be shown.
    *
    * @param page The page number (used for pagination offset)
    */
    private loadOrders(page: number): void {
        this.importOrderService.getOrdersReview(page).subscribe(result => {
            const filteredData = (result?.data ?? []).filter(
                d =>
                    d.externalProvider === 'Manual' &&
                    d.externalTrackingProvider === 'Manual'
            );

            this.gridData = of(filteredData);
            this.pagination = {
                limit: result.limit ?? 10,
                offset: result.offset ?? 0,
                totalCount: filteredData.length
            };
        });
    }

    /**
     * Resizes grid columns to fit the available width.
     */
    resizeToFitColumns(): void {
        this.agGrid?.sizeColumnsToFit();
    }

    /**
     * Reloads the grid data starting from the first page.
     */
    refreshTable(): void {
        this.loadOrders(0);
    }

    /**
     * Alternative initializer to set up the grid and trigger column resizing.
     *
     * @param params The grid initialization parameters
     */
    initializeGrid(params: any): void {
        this.agGrid = params.api;
        this.resizeToFitColumns();
        this.isGridReady = true;
    }

    /**
     * Angular lifecycle hook.
     * Cleans up subscriptions and broadcast listeners when component is destroyed.
     */
    ngOnDestroy(): void {
        this.atComponentDestroy.next();
        this.atComponentDestroy.complete();

        if (this.broadcastUnsubscribeFn) {
            this.broadcastUnsubscribeFn();
        }
    }
}

